import React, { useCallback, useState } from "react";
import JSZip from "jszip";
import {
    Button,
    Modal,
    Form,
    Input,
    Select,
    Tooltip,
    Upload,
    message,
    Row,
    Col,
    Card,
    Collapse,
    Typography,
    Progress,
    Spin,
    Alert,
    Tag,
} from "antd";

import {
    InboxOutlined,
    DeleteOutlined,
    CloudUploadOutlined,
    FileZipOutlined,
    PictureOutlined,
    SwapOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getInspirationCategorydata } from "../store/category/category-slice";
import {
    clearGalleryList,
    createInspirationGallery,
    getInspirationGalleryData,
} from "../store/inspirationGallery/inspiration-gallery-slice";
import { getAttachmentPreUrl, uploadAttachmentToAWS } from "../service/inspiration-gallery-service";
import {
    clearInspirationBulkSnapshot,
    persistInspirationBulkGalleryCurrentItem,
    persistInspirationBulkGalleryStart,
} from "../service/inspiration-gallery-upload-session";
import {
    INSPIRATION_GALLERY_PROGRESS_DISMISSED_MESSAGE,
    waitForInspirationGalleryUploadDone,
} from "../service/inspiration-gallery-upload-sse";
import {
    endBulkGalleryJob,
    setBulkGalleryCurrentItem,
    startBulkGalleryJob,
} from "../store/uploadProgress/uploadProgressSlice";

import "../css/inspiration-gallery-bulk-modal.css";

const { Option } = Select;

const titleFromZipFilename = (name = "") => {
    const base = name.replace(/\.zip$/i, "").trim();
    const readable = base.replace(/[_]+/g, " ").replace(/[-]+/g, " ");
    return readable || "Untitled";
};

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const beforeImageUpload = (file) => {
    const isImage =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp";

    if (!isImage) {
        message.error("Only JPG / PNG / WEBP files allowed");
        return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
        message.error("Image must be smaller than 2MB");
        return Upload.LIST_IGNORE;
    }

    return false;
};

/** Skip macOS resource-fork junk inside archives */
const isIgnoredZipPath = (relativePath) => relativePath.includes("__MACOSX/");

/**
 * Reads an uploaded ZIP. If it contains inner .zip files, each becomes its own row File.
 * Otherwise the outer file is one row (single project gallery ZIP).
 */
async function buildRowsFromZipFile(outerFile, keyPrefix = "") {
    const zip = await JSZip.loadAsync(outerFile);
    const innerEntries = [];

    zip.forEach((relativePath, zipEntry) => {
        if (zipEntry.dir) return;
        if (isIgnoredZipPath(relativePath)) return;
        if (!/\.zip$/i.test(relativePath)) return;
        innerEntries.push({ relativePath, zipEntry });
    });

    innerEntries.sort((a, b) => a.relativePath.localeCompare(b.relativePath));

    const ts = Date.now();
    const prefix = keyPrefix || `${ts}-${outerFile.name}-${outerFile.size}`;

    if (innerEntries.length > 0) {
        const rows = [];
        for (let i = 0; i < innerEntries.length; i++) {
            const { relativePath, zipEntry } = innerEntries[i];
            const blob = await zipEntry.async("blob");
            const baseName = relativePath.split("/").pop() || `project-${i + 1}.zip`;
            const innerFile = new File([blob], baseName, { type: "application/zip" });
            rows.push({
                key: `${prefix}-inner-${i}-${baseName}-${innerFile.size}`,
                zipFile: innerFile,
                title: titleFromZipFilename(baseName),
                logoFile: null,
                featureFile: null,
                logoPreview: null,
                featurePreview: null,
                fromArchive: outerFile.name,
                innerPath: relativePath,
            });
        }
        return { rows, expandedFrom: outerFile.name };
    }

    return {
        rows: [
            {
                key: `${prefix}-single`,
                zipFile: outerFile,
                title: titleFromZipFilename(outerFile.name),
                logoFile: null,
                featureFile: null,
                logoPreview: null,
                featurePreview: null,
                fromArchive: null,
                innerPath: null,
            },
        ],
        expandedFrom: null,
    };
}

const BulkAddInspirationGallery = () => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [appList, setAppList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [unpacking, setUnpacking] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, label: "" });
    const { isLoading, categoryList } = useSelector((state) => state.inspirationCategorySlice);

    const generateImageUrl = async (file) => {
        const { name = "", type = "" } = file ?? {};
        const formData = {
            data: [
                {
                    filename: name,
                    contentType: type || "document",
                },
            ],
        };
        const result = await getAttachmentPreUrl(formData);
        const { success = false, data = {} } = result?.data || {};

        if (success) {
            const uploadURL = data?.[0]?.uploadUrl ?? "";
            const publicLink = data?.[0]?.publicLink ?? "";
            const response = await uploadAttachmentToAWS(file, uploadURL);

            if (response?.ok) {
                return publicLink;
            }
            return "";
        }

        return "";
    };

    const resetAll = () => {
        form.resetFields();
        setAppList([]);
        setProgress({ current: 0, total: 0, label: "" });
    };

    const hasUnsavedBulkDraft = () => {
        if (appList.length > 0) return true;
        const { platform, category } = form.getFieldsValue(["platform", "category"]);
        return Boolean(platform) || Boolean(category);
    };

    const closeBulkModalAndReset = () => {
        resetAll();
        setOpen(false);
    };

    /** Mask click, footer Cancel, and header × all use Modal onCancel. */
    const requestCloseBulkModal = () => {
        if (loading || unpacking) {
            message.warning("Please wait — publishing or unpacking is still in progress.");
            return;
        }
        if (!hasUnsavedBulkDraft()) {
            closeBulkModalAndReset();
            return;
        }
        Modal.confirm({
            title: "Discard your draft?",
            icon: <ExclamationCircleOutlined />,
            content:
                "You have selected scope options or added project ZIPs. Closing now will clear this information, and you will need to enter it again.",
            okText: "Yes, close",
            okType: "danger",
            cancelText: "Keep editing",
            centered: true,
            onOk: () => {
                closeBulkModalAndReset();
            },
        });
    };

    const removeApp = useCallback((key) => {
        setAppList((prev) => prev.filter((a) => a.key !== key));
    }, []);

    const updateApp = useCallback((key, patch) => {
        setAppList((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
    }, []);

    const addZipFiles = async (file) => {
        const isZip =
            file.type === "application/zip" || file.name.toLowerCase().endsWith(".zip");
        if (!isZip) {
            message.error("Only ZIP files allowed");
            return Upload.LIST_IGNORE;
        }

        setUnpacking(true);
        try {
            const { rows, expandedFrom } = await buildRowsFromZipFile(file);
            setAppList((prev) => [...prev, ...rows]);
            if (expandedFrom) {
                message.success(
                    `Added ${rows.length} project ZIP(s) from “${expandedFrom}”`
                );
            } else {
                message.success(`Added “${file.name}”`);
            }
        } catch (e) {
            console.error(e);
            message.error("Could not read this ZIP. Use a valid .zip file.");
        } finally {
            setUnpacking(false);
        }
        return false;
    };

    const validateAppList = () => {
        if (appList.length === 0) {
            message.warning("Add at least one ZIP file");
            return false;
        }
        for (let i = 0; i < appList.length; i++) {
            const app = appList[i];
            const title = (app.title || "").trim();
            if (title.length < 3) {
                message.warning(`Row ${i + 1}: title must be at least 3 characters`);
                return false;
            }
            if (!app.logoFile) {
                message.warning(`Row ${i + 1}: logo is required`);
                return false;
            }
            if (!app.featureFile) {
                message.warning(`Row ${i + 1}: feature image is required`);
                return false;
            }
            if (!app.zipFile) {
                message.warning(`Row ${i + 1}: ZIP is missing`);
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (values) => {
        if (!validateAppList()) return;

        setLoading(true);
        const total = appList.length;
        setProgress({ current: 0, total, label: "" });
        dispatch(startBulkGalleryJob({ total }));
        persistInspirationBulkGalleryStart({ total });
        /* Close as soon as the bottom progress job starts */
        setOpen(false);

        let bulkSuccessHoldTeardown = false;

        try {
            for (let i = 0; i < appList.length; i++) {
                const app = appList[i];
                const rowTitle = (app.title || "").trim() || titleFromZipFilename(app.zipFile?.name);
                dispatch(
                    setBulkGalleryCurrentItem({
                        index: i + 1,
                        title: rowTitle,
                    })
                );
                persistInspirationBulkGalleryCurrentItem({ index: i + 1, title: rowTitle });
                setProgress({
                    current: i + 1,
                    total,
                    label: app.title,
                });

                const projectLogoUrl = await generateImageUrl(app.logoFile);
                if (!projectLogoUrl) {
                    message.error(`Logo upload failed for "${app.title}"`);
                    setOpen(true);
                    return;
                }

                const projectFeatureImageUrl = await generateImageUrl(app.featureFile);
                if (!projectFeatureImageUrl) {
                    message.error(`Feature image upload failed for "${app.title}"`);
                    setOpen(true);
                    return;
                }

                const formData = new FormData();
                formData.append("platform", values.platform);
                formData.append("category", values.category);
                formData.append("projectTitle", (app.title || "").trim());
                formData.append("projectLogo", projectLogoUrl);
                formData.append("projectFeatureImage", projectFeatureImageUrl);
                formData.append("projectGallery", app.zipFile);

                const { data } = await createInspirationGallery(formData);

                if (!data?.success || !data?.uploadId) {
                    message.error(
                        data?.message ||
                            `Create failed for "${app.title}"`
                    );
                    setOpen(true);
                    return;
                }

                await waitForInspirationGalleryUploadDone(data.uploadId, dispatch, {
                    refreshListOnDone: false,
                    persistSession: true,
                    bulkGalleryMode: true,
                });
            }

            message.success(`Published ${total} gallery item(s)`);
            dispatch(clearGalleryList());
            dispatch(getInspirationGalleryData({ skip: 0, limit: 24 }));
            resetAll();
            bulkSuccessHoldTeardown = true;
            await new Promise((r) => setTimeout(r, 5000));
        } catch (err) {
            if (err?.message === INSPIRATION_GALLERY_PROGRESS_DISMISSED_MESSAGE) {
                return;
            }
            console.error("Bulk submit error:", err);
            message.error(err?.message || "Something went wrong");
            setOpen(true);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, label: "" });
            if (!bulkSuccessHoldTeardown) {
                dispatch(endBulkGalleryJob());
                clearInspirationBulkSnapshot();
            }
        }
        if (bulkSuccessHoldTeardown) {
            dispatch(endBulkGalleryJob());
            clearInspirationBulkSnapshot();
        }
    };

    const collapseItems = appList.map((app, index) => ({
        key: app.key,
        label: (
            <div className="inspiration-bulk-panel-head">
                <div className="inspiration-bulk-panel-head__main">
                    <span className="inspiration-bulk-panel-idx">{index + 1}</span>
                    <span
                        className="inspiration-bulk-panel-title"
                        title={app.title || titleFromZipFilename(app.zipFile?.name)}
                    >
                        {app.title || titleFromZipFilename(app.zipFile?.name)}
                    </span>
                </div>
                <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        removeApp(app.key);
                    }}
                />
            </div>
        ),
        children: (
            <div className="inspiration-bulk-panel-body">
                <div className="inspiration-bulk-zip-row">
                    <div className="inspiration-bulk-zip-row__meta">
                        <div className="inspiration-bulk-zip-name">
                            <FileZipOutlined />
                            <span>{app.zipFile?.name}</span>
                            {app.fromArchive ? (
                                <Tag className="inspiration-bulk-zip-tag">From bundle</Tag>
                            ) : (
                                <Tag className="inspiration-bulk-zip-tag">Single ZIP</Tag>
                            )}
                        </div>
                        {app.fromArchive ? (
                            <div className="inspiration-bulk-zip-source">
                                {app.fromArchive}
                                {app.innerPath ? ` · ${app.innerPath}` : ""}
                            </div>
                        ) : null}
                    </div>
                    <Upload
                        accept=".zip"
                        showUploadList={false}
                        maxCount={1}
                        disabled={unpacking}
                        beforeUpload={async (file) => {
                            const isZip =
                                file.type === "application/zip" ||
                                file.name.toLowerCase().endsWith(".zip");
                            if (!isZip) {
                                message.error("Only ZIP files allowed");
                                return Upload.LIST_IGNORE;
                            }
                            setUnpacking(true);
                            try {
                                const { rows } = await buildRowsFromZipFile(
                                    file,
                                    `replace-${app.key}`
                                );
                                setAppList((prev) => {
                                    const others = prev.filter((a) => a.key !== app.key);
                                    if (rows.length === 1) {
                                        return [
                                            ...others,
                                            {
                                                ...rows[0],
                                                key: app.key,
                                            },
                                        ];
                                    }
                                    return [...others, ...rows];
                                });
                                if (rows.length > 1) {
                                    message.success(
                                        `Replaced with ${rows.length} project ZIP(s) from “${file.name}”`
                                    );
                                } else {
                                    message.success(`Replaced with “${rows[0].zipFile.name}”`);
                                }
                            } catch (e) {
                                console.error(e);
                                message.error("Could not read this ZIP.");
                            } finally {
                                setUnpacking(false);
                            }
                            return false;
                        }}
                    >
                        <Tooltip
                            title={
                                unpacking
                                    ? "Unpacking…"
                                    : "Choose another .zip for this row (replaces this project archive)"
                            }
                        >
                            <span style={{ display: "inline-block" }}>
                                <Button
                                    type="default"
                                    size="small"
                                    className="inspiration-bulk-replace-btn"
                                    icon={<SwapOutlined />}
                                    disabled={unpacking}
                                >
                                    Replace ZIP
                                </Button>
                            </span>
                        </Tooltip>
                    </Upload>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label className="inspiration-bulk-field-label" htmlFor={`bulk-title-${app.key}`}>
                        Project title
                    </label>
                    <Input
                        id={`bulk-title-${app.key}`}
                        size="large"
                        className="inspiration-bulk-title-input"
                        placeholder="Shown in the gallery (defaults from ZIP name)"
                        value={app.title}
                        onChange={(e) => updateApp(app.key, { title: e.target.value })}
                    />
                </div>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <Card size="small" title="Logo" className="inspiration-bulk-asset-card">
                            <Upload
                                className="inspiration-bulk-upload-tile"
                                listType="picture-card"
                                showUploadList={false}
                                beforeUpload={async (file) => {
                                    if (beforeImageUpload(file) === false) {
                                        updateApp(app.key, {
                                            logoFile: file,
                                            logoPreview: await getBase64(file),
                                        });
                                    }
                                    return false;
                                }}
                            >
                                {app.logoPreview ? (
                                    <img src={app.logoPreview} alt="Logo preview" />
                                ) : (
                                    <div className="inspiration-bulk-upload-tile__inner">
                                        <PictureOutlined />
                                        <span>Add logo</span>
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                            JPG, PNG, WEBP · max 2MB
                                        </Typography.Text>
                                    </div>
                                )}
                            </Upload>
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card
                            size="small"
                            title="Feature image"
                            className="inspiration-bulk-asset-card"
                        >
                            <Upload
                                className="inspiration-bulk-upload-tile"
                                listType="picture-card"
                                showUploadList={false}
                                beforeUpload={async (file) => {
                                    if (beforeImageUpload(file) === false) {
                                        updateApp(app.key, {
                                            featureFile: file,
                                            featurePreview: await getBase64(file),
                                        });
                                    }
                                    return false;
                                }}
                            >
                                {app.featurePreview ? (
                                    <img src={app.featurePreview} alt="Feature preview" />
                                ) : (
                                    <div className="inspiration-bulk-upload-tile__inner">
                                        <PictureOutlined />
                                        <span>Add feature</span>
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                            JPG, PNG, WEBP · max 2MB
                                        </Typography.Text>
                                    </div>
                                )}
                            </Upload>
                        </Card>
                    </Col>
                </Row>
            </div>
        ),
    }));

    return (
        <>
            <Button
                type="primary"
                className="inspiration-add-gallery-btn"
                icon={<CloudUploadOutlined />}
                onClick={() => setOpen(true)}
            >
                Add Gallery
            </Button>

            <Modal
                title={
                    <div className="inspiration-bulk-modal__title-row">
                        <div className="inspiration-bulk-modal__icon-wrap">
                            <CloudUploadOutlined />
                        </div>
                        <div>
                            <div className="inspiration-bulk-modal__title">Bulk upload</div>
                            <div className="inspiration-bulk-modal__subtitle">
                                One platform and category for every project in this batch. Expand
                                each row to add title, logo, and feature image.
                            </div>
                        </div>
                    </div>
                }
                open={open}
                onCancel={requestCloseBulkModal}
                onOk={() => form.submit()}
                okText={
                    appList.length === 0
                        ? "Publish"
                        : `Publish ${appList.length} ${appList.length === 1 ? "item" : "items"}`
                }
                width="min(92vw, 880px)"
                destroyOnClose
                centered
                className="inspiration-bulk-modal"
                okButtonProps={{
                    loading,
                    disabled: loading || unpacking || appList.length === 0,
                    className: "no-hover-primary",
                }}
                cancelButtonProps={{ disabled: loading || unpacking }}

            >
                <Alert
                    className="inspiration-bulk-alert"
                    type="info"
                    showIcon
                    message="How it works"
                    description={
                        <>
                            Upload a <strong>bundle ZIP</strong> (many <code>.zip</code> files
                            inside) or individual project ZIPs. Inner archives open automatically
                            into separate rows. Images upload first; each row’s gallery ZIP is sent
                            like single Add.
                        </>
                    }
                />

                <Form layout="vertical" form={form} onFinish={handleSubmit}>
                    <div className="inspiration-bulk-section">
                        <div className="inspiration-bulk-section__head">
                            <span className="inspiration-bulk-section__index">1</span>
                            <div>
                                <span className="inspiration-bulk-section__title">Scope</span>
                                <span className="inspiration-bulk-section__hint">
                                    Applies to all projects you add in this modal
                                </span>
                            </div>
                        </div>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Platform"
                                    name="platform"
                                    rules={[{ required: true, message: "Platform is required" }]}
                                >
                                    <Select size="large" placeholder="Select platform">
                                        <Option value="Web">Web</Option>
                                        <Option value="Mobile">Mobile</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Category"
                                    name="category"
                                    rules={[{ required: true, message: "Category is required" }]}
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select category"
                                        loading={isLoading}
                                        onDropdownVisibleChange={(dropdownOpen) => {
                                            if (dropdownOpen) dispatch(getInspirationCategorydata());
                                        }}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        options={categoryList?.map((item) => ({
                                            value: item._id,
                                            label: item.name,
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="inspiration-bulk-section">
                        <div className="inspiration-bulk-section__head">
                            <span className="inspiration-bulk-section__index">2</span>
                            <div>
                                <span className="inspiration-bulk-section__title">Add archives</span>
                                <span className="inspiration-bulk-section__hint">
                                    Drag in a bundle or multiple ZIPs — nested files become rows
                                </span>
                            </div>
                        </div>
                        <div className="inspiration-bulk-zip-wrap">
                            <Upload.Dragger
                                className="inspiration-bulk-zip-dragger"
                                accept=".zip"
                                multiple
                                showUploadList={false}
                                beforeUpload={addZipFiles}
                                disabled={loading || unpacking}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined style={{ fontSize: 40, color: "#1677ff" }} />
                                </p>
                                <p className="inspiration-bulk-zip-dragger__title">
                                    Drop ZIP files here
                                </p>
                                <p className="inspiration-bulk-zip-dragger__hint">
                                    Or click to browse. Bundle archives unpack into one row per
                                    inner <code>.zip</code>.
                                </p>
                            </Upload.Dragger>
                            {unpacking ? (
                                <div className="inspiration-bulk-zip-overlay">
                                    <Spin tip="Reading archive…" />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Form>

                {appList.length > 0 ? (
                    <>
                        <div className="inspiration-bulk-list-head">
                            <span className="inspiration-bulk-list-head__title">
                                Projects ({appList.length})
                            </span>
                            <Tag color="processing">Expand to edit each</Tag>
                        </div>
                        <Collapse
                            className="inspiration-bulk-collapse"
                            expandIconPosition="end"
                            items={collapseItems}
                            defaultActiveKey={appList.map((a) => a.key)}
                        />
                    </>
                ) : (
                    <div className="inspiration-bulk-empty-hint">
                        No projects yet. Add ZIP files in step 2 — they will appear here as
                        expandable cards.
                    </div>
                )}

                {loading && progress.total > 0 && (
                    <div className="inspiration-bulk-progress">
                        <Progress
                            percent={Math.round((progress.current / progress.total) * 100)}
                            status="active"
                            strokeColor="#1677ff"
                        />
                        <span className="inspiration-bulk-progress-meta">
                            Publishing {progress.current} of {progress.total}
                            {progress.label ? ` · ${progress.label}` : ""}
                        </span>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default BulkAddInspirationGallery;
